// Dependencias
import DataTable, { Api } from 'datatables.net-bs5';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { AxiosError } from 'axios';

// Utils
import { loadLoader } from '../utils/loader';
import { debounce } from '../utils/debounce';
import { validInputStyle, ValidationsTypes } from '../utils/stylesInputs';
import {
  drawTable,
  formatPhoneNumber,
  drawShowAsingFolio,
  drawApiToggle,
  drawBlockToggle
} from '../utils/drawTable';

// Keys
import { URL_WHMCS } from '../config/keys.config';

// Messages
import {
  PlaceholderMessageSearch,
  OptionsMessages,
  ErrorMessagesToast,
  GenericMessage
} from '../config/Messages.config';

// Services
import * as clientService from '../service/client.service';
import { ClientWHMCS, HttpResponse, getClientDetails } from '../service/whmcs.service';
import { fetchFoliosByMonth } from '../service/panel.service';

// Config
import {
  alertConfigurations,
  toastConfiguration
} from '../config/sweetAlert.config';
import { generalConfig } from '../config/datatable.config';
import { randomPassword } from '../utils/randomPassword';

// Elementos HTML
const tableUsersElement = document.querySelector<HTMLTableElement>('#users-list')!;
const currentMountElement = document.querySelector<HTMLSpanElement>('#current-month')!;
const formSearchElement = document.querySelector<HTMLFormElement>('#form-search')!;
const selectSearchElement = document.querySelector<HTMLSelectElement>('#selectSearch')!;
const inputSearchElement = document.querySelector<HTMLInputElement>('#inputSearch')!;
const addUserButtonElement = document.querySelector<HTMLButtonElement>('#button-add-client')!;
const tableBodyElements = tableUsersElement.querySelector('tbody')!;
const monthSelectorElement = document.querySelector<HTMLSelectElement>('#month-selector')!;
const totalAssignCurrentMonthElement = document.querySelector<HTMLSpanElement>('#total-asign-current-month-folios')!;

// Variables Globales
let clientsTable: Api<string[]>;
let currentPage = 1;
let lengthTable = 10;
const currentChangeEvents = ['change', 'keyup'];
const toast = Swal.mixin(toastConfiguration);

export async function main() {
  document.addEventListener('DOMContentLoaded', async() => {
    await Promise.all([
      // Configuración de Tabla
      (() =>
        (clientsTable = new DataTable(tableUsersElement, generalConfig)))(),
      // Configuración de fechas
      (() =>
        (currentMountElement.innerText = getMonth(new Date().getMonth())))(),
      // Poblar selector de meses
      (() => populateMonthSelector())(),
      // Crea a todos los boton mustren el modal de datos
      makeBtnShowButtonAssing(),
      // Configura los listeners para los toggles de API
      makeApiToggleListeners(),
      // Configura los listeners para los toggles de bloqueo
      makeBlockToggleListeners()
    ]);

    // Cambio de mes para actualizar la métrica
    monthSelectorElement.addEventListener('change', handleChangeMonthAssignedFolios);

    clientsTable.on('draw', async() => {
      const info = clientsTable.page.info();

      // Si la Pagina no es mayor a los datos establecidos, sale de la función
      if (!(info.page + 1 > currentPage)) return;

      // Optengo los datos de los filtros
      const json = Object.fromEntries(
        new FormData(formSearchElement)
      ) as ParamsSearch;
      currentPage = info.page + 1;

      loadLoader({});

      const addListOfClients = await clientService.searchClientsBy({
        params: {
          [json.selectSearch!]: json.valueToSearch!,
          includeKiubix: 'true'
        },
        page: currentPage + 1,
        limit: lengthTable
      });
      drawTable({
        data: addListOfClients,
        table: clientsTable,
        addFinale: true,
        format: formatClients
      });
      makeBtnShowButtonAssing();
      makeApiToggleListeners();
      makeBlockToggleListeners();
      loadLoader({ start: false });
    });

    clientsTable.on('length', async(_, __, len) =>  {
      lengthTable = len;

      // Optengo los datos de los filtros
      const json = Object.fromEntries(
        new FormData(formSearchElement)
      ) as ParamsSearch;

      loadLoader({});
      const addListOfClients = await clientService.searchClientsBy({
        params: {
          [json.selectSearch!]: json.valueToSearch!,
          includeKiubix: 'true'
        },
        limit: lengthTable * 2
      });
      drawTable({
        data: addListOfClients,
        table: clientsTable,
        format: formatClients
      });
      makeBtnShowButtonAssing();
      makeApiToggleListeners();
      makeBlockToggleListeners();
      currentPage = 1;
      loadLoader({ start: false });
    });
  });

  // Funcionalidad de Validacion de Inputs
  [selectSearchElement, inputSearchElement].forEach((element) => {
    currentChangeEvents.forEach((event) => {
      element.addEventListener(event, async(e) => {
        await Promise.all([
          (() => {
            const currentTarget = e.target;
            if (selectSearchElement.value === TypeInputOptions.ALL) {
              inputSearchElement.value = '';
              validInputStyle({
                type: ValidationsTypes.RESET,
                container: inputSearchElement.parentElement as HTMLDivElement,
                input: inputSearchElement
              });
            }

            if (currentTarget === inputSearchElement) return;
            if (event !== 'change') return;

            const valueSearch = selectSearchElement.value.toUpperCase() as keyof typeof PlaceholderMessageSearch;
            inputSearchElement.placeholder = PlaceholderMessageSearch[valueSearch];
          })(),
          (() => {
            const target = e.target as HTMLSelectElement | HTMLInputElement;
            const container = target.parentElement as HTMLDivElement;
            const input = target;

            if (selectSearchElement.value === TypeInputOptions.ALL) {
              validInputStyle({
                type: ValidationsTypes.VALID,
                container,
                input
              });
              return (inputSearchElement.disabled = true);
            }

            if (inputSearchElement.disabled)
              inputSearchElement.disabled = false;
            const isEmpty = target.value.length <= 0;
            if (isEmpty) {
              return validInputStyle({
                type: ValidationsTypes.RESET,
                container,
                input
              });
            }

            return validInputStyle({
              type: ValidationsTypes.VALID,
              container,
              input
            });
          })()
        ]);
      });
    });
  });

  // Busqueda de información de clientes
  formSearchElement.addEventListener('submit', async(e) => {
    e.preventDefault();

    // Se optiene la información del formulario
    const target = e.target as HTMLFormElement;
    const json = Object.fromEntries(new FormData(target)) as ParamsSearch;

    // Se aplica al Loader de la aplicación
    loadLoader({});

    // Se realiza la optencion de valores para el servicio
    currentPage = 1;
    const listOfClients = await clientService.searchClientsBy({
      params: {
        [json.selectSearch]: json.valueToSearch,
        includeKiubix: 'true'
      },
      limit: 20
    });
    drawTable({
      table: clientsTable,
      data: listOfClients,
      format: formatClients
    });
    makeBtnShowButtonAssing();
    makeApiToggleListeners();
    makeBlockToggleListeners();
    loadLoader({ start: false });
  });

  // Funcionalidad del Botón de Agregar Usuario
  addUserButtonElement.addEventListener('click', async() => {
    try {
      const { value: client } = await Swal.fire<ClientWHMCS>({
        ...alertConfigurations,
        allowEscapeKey: false,
        heightAuto: true,
        showDenyButton: false,
        confirmButtonText: OptionsMessages.ACEPTAR,
        icon: 'question',
        text: 'Ingresa el ID del Cliente del Sistema WHMCS (idclient)',
        input: 'number' as any,
        showLoaderOnConfirm: true,
        inputAttributes: {
          autocapitalize: 'off',
          min: '1'
        },
        preConfirm: async(id) => {
          if (!id || id <= 0) {
            Swal.showValidationMessage('El ID del Cliente es obligatorio');
            return;
          }
          
          try {
            const clientDetails = await getClientDetails(id);
            return { ...clientDetails.client, idclient: id };
          } catch (error) {
            if (error instanceof AxiosError) {
              const { response } = error as AxiosError<HttpResponse<ClientWHMCS>>;
              const { message } = response!.data;

              Swal.showValidationMessage(message);
              return;
            }
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      // Si no se obtiene un cliente, se sale de la función
      if (!client) return;

      const result = await Swal.fire({
        ...alertConfigurations,
        allowEscapeKey: false,
        heightAuto: true,
        confirmButtonText: OptionsMessages.ACEPTAR,
        denyButtonText: OptionsMessages.CANCELAR,
        titleText: 'INFORMACIÓN',
        html: `
          <div class="mb-3 ">
            <p class="fs-5"><span class="fw-bold">Nombre:</span> ${client.nombre} ${client.apellidos}</p>
            <p class="fs-5"><span class="fw-bold">Correo Electronico:</span> ${client.email}</p>
            <p class="fs-5"><span class="fw-bold">Teléfono:</span> ${formatPhoneNumber(client.telefono)}</p>
          </div>
          <div class="mb-3">
            <p class="fs-4 fw-bold">¿Desea continuar con la creación del Cliente?</p>
          </div>
        `
      });

      if (result.isDenied || !result.isConfirmed) return;

      loadLoader({ start: true, topScroll: window.scrollY });
      const newClient = {
        name: client.nombre!,
        last_name: client.apellidos!,
        email: client.email!,
        password: randomPassword({ length: 20 }),
        lang: 'es',
        phone: client.telefono!,
        countryCallingCode: '+52',
        equipo: 'Windows',
        country: 'México',
        clientIdWHMCS: client.idclient
      };
      await clientService.createClient(newClient);
      
      loadLoader({ start: false });
      await Swal.fire({
        ...alertConfigurations,
        showDenyButton: false,
        confirmButtonText: OptionsMessages.ACEPTAR,
        icon: 'success',
        text: 'El cliente se ha creado correctamente'
      });
      window.location.reload();
    } catch (error) {
      loadLoader({ start: false, topScroll: window.scrollY });
      if (error instanceof AxiosError) {
        const { status, data } = error.response!;
      
        Swal.fire({
          ...alertConfigurations,
          icon: 'error',
          title: status === 400 ? 'ERROR DE VALIDACIÓN' : GenericMessage.ERROR,
          text: data.message,
          showConfirmButton: true,
          showDenyButton: false
        });
        return;
      } 

      console.error(error);
    }
  });
}

/**
 *
 */
function makeBtnShowButtonAssing() {
  tableBodyElements.removeEventListener('click', asingFolios, {
    capture: true
  });
  tableBodyElements.addEventListener('click', asingFolios, { capture: true });
}

/**
 * Configura los event listeners para los toggles de API
 */
function makeApiToggleListeners() {
  // Remover listeners anteriores para evitar duplicados
  const existingToggles = document.querySelectorAll('.api-toggle');
  existingToggles.forEach((toggle) => {
    toggle.removeEventListener('change', handleApiToggleChange);
  });

  // Agregar listeners a todos los toggles de API
  const apiToggles = document.querySelectorAll<HTMLInputElement>('.api-toggle');
  apiToggles.forEach((toggle) => {
    toggle.addEventListener('change', handleApiToggleChange);
  });
}

/**
 * Maneja el cambio del toggle de API
 * @param e Evento de cambio
 */
async function handleApiToggleChange(e: Event) {
  const target = e.target as HTMLInputElement;
  const userId = parseInt(target.dataset.userId || '0', 10);
  const enabled = target.checked;

  if (!userId) {
    console.error('ID de usuario no válido');
    return;
  }

  try {
    loadLoader({});
    
    // Deshabilitar el toggle mientras se procesa la petición
    target.disabled = true;

    await clientService.toggleUserApi(userId, enabled);

    // Mostrar mensaje de éxito
    toast.fire({
      icon: 'success',
      title: enabled ? 'API activada correctamente' : 'API desactivada correctamente'
    });

    // Habilitar el toggle nuevamente
    target.disabled = false;
  } catch (error) {
    // Revertir el estado del toggle en caso de error
    target.checked = !enabled;
    target.disabled = false;

    if (error instanceof AxiosError) {
      const { data } = error.response!;
      toast.fire({
        icon: 'error',
        title: data?.message || 'Error al actualizar el estado de la API'
      });
    } else {
      toast.fire({
        icon: 'error',
        title: ErrorMessagesToast.ERROR
      });
    }
  } finally {
    loadLoader({ start: false });
  }
}

/**
 * Configura los event listeners para los toggles de bloqueo
 */
function makeBlockToggleListeners() {
  // Remover listeners anteriores para evitar duplicados
  const existingToggles = document.querySelectorAll('.block-toggle');
  existingToggles.forEach((toggle) => {
    toggle.removeEventListener('change', handleBlockToggleChange);
  });

  // Agregar listeners a todos los toggles de bloqueo
  const blockToggles = document.querySelectorAll<HTMLInputElement>('.block-toggle');
  blockToggles.forEach((toggle) => {
    toggle.addEventListener('change', handleBlockToggleChange);
  });
}

/**
 * Maneja el cambio del toggle de bloqueo
 * @param e Evento de cambio
 */
async function handleBlockToggleChange(e: Event) {
  const target = e.target as HTMLInputElement;
  const userId = parseInt(target.dataset.userId || '0', 10);
  const blocked = target.checked;

  if (!userId) {
    console.error('ID de usuario no válido');
    return;
  }

  const result = await Swal.fire({
    ...alertConfigurations,
    title: 'Confirmación',
    text: blocked
      ? '¿Deseas bloquear a este usuario? No podrá iniciar sesión en Signly.'
      : '¿Deseas desbloquear a este usuario?',
    icon: 'info'
  });

  if (!result.isConfirmed) {
    target.checked = !blocked;
    return;
  }

  try {
    loadLoader({});

    // Deshabilitar el toggle mientras se procesa la petición
    target.disabled = true;

    await clientService.changeStatusUser({
      type: blocked
        ? clientService.ActionTypesStatus.BLOCK
        : clientService.ActionTypesStatus.UNBLOCK,
      params: { id: String(userId) }
    });

    // Mostrar mensaje de éxito
    toast.fire({
      icon: 'success',
      title: blocked ? 'Usuario bloqueado correctamente' : 'Usuario desbloqueado correctamente'
    });

    // Habilitar el toggle nuevamente
    target.disabled = false;
  } catch (error) {
    // Revertir el estado del toggle en caso de error
    target.checked = !blocked;
    target.disabled = false;

    if (error instanceof AxiosError) {
      const { data } = error.response!;
      toast.fire({
        icon: 'error',
        title: data?.message || 'Error al actualizar el estado del usuario'
      });
    } else {
      toast.fire({
        icon: 'error',
        title: ErrorMessagesToast.ERROR
      });
    }
  } finally {
    loadLoader({ start: false });
  }
}

/**
 *
 * @param e
 * @returns
 */
async function asingFolios(e: MouseEvent) {
  const targetElement = e.target as HTMLElement;

  // Si no son Elementos Button o I, se salen del manejador de eventos
  if (!(targetElement.tagName in TypeElementClicked)) return;

  // Se obtiene el id del cliente.
  const idClient =
    targetElement.tagName === TypeElementClicked.I
      ? targetElement.parentElement?.dataset.idClient
      : targetElement.dataset.idClient;

  const id =
    targetElement.tagName === TypeElementClicked.I
      ? targetElement.parentElement?.dataset.id
      : targetElement.dataset.id;

  // Se realiza la peticion de los datos al usuario
  const result = await Swal.fire({
    ...alertConfigurations,
    confirmButtonText: OptionsMessages.ACEPTAR,
    denyButtonText: OptionsMessages.CANCELAR,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    heightAuto: true,
    title: '<p class="h1">Inserta los siguientes datos:</p>',
    html: `
      <div class="mb-3">
        <div class="input-group mb-1 position-relative">
          <span class="input-group-text"><i class="bi bi-receipt"></i></span>
          <input type="number" class="form-control" placeholder="Inserta el Id de la ODS" id="invoice-id" list="order-list" data-id-client="${idClient}" name="invoiceId">
          <div id="autocomplete-results" class="autocomplete-list"></div>
        </div>
        <div id="invoice-status-feedback" class="form-text text-start mt-1 fw-bold" style="min-height: 1.5em;"></div>
        <div class="form-check form-switch text-start">
          <input class="form-check-input" type="checkbox" id="search-to-ordernum">
          <label class="form-check-label" for="search-to-ordernum">Buscar por # de orden</label>
        </div>
      </div>
      <div class="input-group mb-3">
        <span class="input-group-text"><i class="bi bi-123"></i></span>
        <input type="number" class="form-control" placeholder="Inserta el número de folios" id="folios" name="folios">
      </div>
       <div class="input-group mb-3">
        <span class="input-group-text"><i class="bi bi-cash-coin"></i></span>
        <input type="text" class="form-control" placeholder="Inserta el monto de pago" id="amount" name="amount">
      </div>
    `,
    didOpen() {
      const switchInput = document.querySelector<HTMLInputElement>(
        '#search-to-ordernum'
      )!;
      const inputs = getInputsToValidate();
      const invoiceElement = inputs.at(-1)!;

      // Aplico los elementos de validacion de Inputs
      inputs.forEach((element) =>
        currentChangeEvents.forEach((event) =>
          element.addEventListener(event, handdleEventToChangeInput)
        )
      );
      invoiceElement.addEventListener('input', haddleFunctionSearchOders);
      invoiceElement.addEventListener('input', handleCheckInvoiceStatus);

      switchInput.addEventListener('change', handdleSearchToOrderNum);
    },
    willClose() {
      const switchInput = document.querySelector<HTMLInputElement>(
        '#search-to-ordernum'
      )!;
      const inputs = getInputsToValidate();
      const invoiceElement = inputs.at(-1)!;

      // Se eliminand los eventos de validación
      inputs.forEach((element) =>
        currentChangeEvents.forEach((event) =>
          element.removeEventListener(event, handdleEventToChangeInput)
        )
      );
      invoiceElement.removeEventListener('input', haddleFunctionSearchOders);
      invoiceElement.removeEventListener('input', handleCheckInvoiceStatus);

      switchInput.removeEventListener('change', handdleSearchToOrderNum);
    },
    preConfirm: async() => {
      // Se obtienen los valores que se le solicitan al cliente.
      const inputs = getInputsToValidate();

      // Valido que los datos no esten vacios
      const thereAreEmtyInputs = inputs.some((input) => !input.value);
      if (thereAreEmtyInputs) {
        inputs.forEach((input) => {
          if (input.value) return;

          return validInputStyle({
            input,
            container: input.parentElement as HTMLDivElement,
            type: ValidationsTypes.INVALID
          });
        });

        return false;
      }

      return inputs.reduce(
        (object, input) => ({ ...object, [input.name]: input.value }),
        {}
      );
    },
    allowOutsideClick: () => !Swal.isLoading()
  });

  if (!result.isConfirmed) return;

  loadLoader({});
  const data: AssingFoliosData = { ...result.value, userId: id };
  const clientRespose = await clientService.asingFolios({ params: data });

  if (clientRespose) {
    const {
      data: { message: text },
      status
    } = clientRespose;

    let alertOptions: SweetAlertOptions = {
      ...alertConfigurations,
      showDenyButton: false,
      confirmButtonText: OptionsMessages.ACEPTAR,
      text
    };
    alertOptions =
      status < 400
        ? { ...alertOptions, icon: 'success' }
        : { ...alertOptions, icon: 'error' };

    await Swal.fire(alertOptions);
    window.location.reload();
  } else {
    toast.fire({
      icon: 'error',
      title: ErrorMessagesToast.ERROR
    });
  }

  loadLoader({ start: false });
}

/**
 *
 */
function getInputsToValidate() {
  const numFolios = document.querySelector<HTMLInputElement>('#folios')!;
  const amount = document.querySelector<HTMLInputElement>('#amount')!;
  const invoiceId = document.querySelector<HTMLInputElement>('#invoice-id')!;

  return [numFolios, amount, invoiceId];
}

/**
 *
 * @param e
 */
function handdleEventToChangeInput(e: Event) {
  const target = e.target as HTMLInputElement;
  const container = target.parentElement! as HTMLDivElement;
  const currentValue = target.value;

  if (currentValue.length <= 0) {
    return validInputStyle({
      input: target,
      container,
      type: ValidationsTypes.RESET
    });
  }

  return validInputStyle({
    input: target,
    container,
    type: ValidationsTypes.VALID
  });
}

/**
 *
 * @param e
 */
function handdleSearchToOrderNum(e: Event) {
  const target = e.target as HTMLInputElement;
  const inputs = getInputsToValidate();
  const invoiceId = inputs.at(-1)!;

  if (target.checked) {
    invoiceId.placeholder = 'Inserta el Numero de la orden';
    invoiceId.dataset.search = 'yes';
  } else {
    invoiceId.placeholder = 'Inserta el ID de la ODS';
    invoiceId.dataset.search = 'no';
  }
}

/**
 *
 */
const haddleFunctionSearchOders = debounce(async(e: Event) => {
  const [numFolios, amount, invoiceId] = getInputsToValidate();
  const target = e.target as HTMLInputElement;
  const containerInput = target.parentElement as HTMLDivElement;
  const orderList = document.querySelector<HTMLDivElement>(
    '#autocomplete-results'
  )!;
  const { idClient } = target.dataset;
  const numOrder = target.value;
  const urlWhmcs = new URL('/admin/orders.php?action=view', URL_WHMCS);

  // Valido si se realiza la busqueda de los datos
  if (!target.dataset.search || target.dataset.search === 'no') return;

  // Valido que el valor no sea vacio
  if (target.value.length <= 0) {
    orderList.innerHTML = '';
    orderList.style.display = 'none';

    numFolios.value = '';
    amount.value = '';
    return;
  }

  Swal.showLoading();
  const { orders } = await clientService.getOrderByClient({
    params: { numOrder, id: idClient! }
  });

  if (orders.length <= 0) {
    Swal.hideLoading();
    return validInputStyle({
      input: target,
      container: containerInput,
      type: ValidationsTypes.INVALID
    });
  }

  orderList.innerHTML = '';
  const htmlOrders = orders.map((order) => {
    const element = document.createElement('div');
    urlWhmcs.searchParams.set('id', `${order.id}`);

    element.classList.add('autocomplete-item');
    element.innerHTML = `Número de Orden ${order.ordernum} - <a href="${urlWhmcs.href}" target="_blank">¡Verificar Aqui!</a>`;

    element.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A') return;
      
      // Al seleccionar, desactivamos el modo búsqueda para validar el ID real
      const switchInput = document.querySelector<HTMLInputElement>('#search-to-ordernum')!;
      switchInput.checked = false;
      switchInput.dispatchEvent(new Event('change'));

      invoiceId.value = `${order.invoiceid}`;

      orderList.innerHTML = '';
      orderList.style.display = 'none';

      // Disparar validación de estado
      invoiceId.dispatchEvent(new Event('input'));

      numFolios.focus();
    });

    return element;
  });
  orderList.append(...htmlOrders);
  orderList.style.display = 'block';

  Swal.hideLoading();
}, 450);

/**
 * Verifica el estado de la factura seleccionada o escrita
 */
const handleCheckInvoiceStatus = debounce(async(e: Event) => {
  const target = e.target as HTMLInputElement;
  const feedbackElement = document.querySelector<HTMLDivElement>('#invoice-status-feedback')!;
  const invoiceId = target.value;

  // Si está vacío, limpiamos
  if (!invoiceId) {
    feedbackElement.innerText = '';
    return;
  }

  // Si estamos en modo búsqueda de orden, no validamos el status de la factura
  // (porque el valor es el número de orden, no el ID de la factura)
  if (target.dataset.search === 'yes') {
    feedbackElement.innerText = '';
    return;
  }

  feedbackElement.innerHTML = '<div class="spinner-border spinner-border-sm text-secondary" role="status"></div> Verificando...';
  feedbackElement.className = 'form-text text-start mt-1 text-muted';

  const status = await clientService.checkInvoiceStatus(invoiceId);

  if (status.exists) {
    if (status.isPaid) {
      feedbackElement.innerHTML = '<i class="bi bi-exclamation-triangle-fill"></i> <b>Atención:</b> Esta factura ya está PAGADA y asignada.';
      feedbackElement.className = 'form-text text-start mt-1 text-danger';
    } else {
      feedbackElement.innerHTML = '<i class="bi bi-info-circle-fill"></i> <b>Nota:</b> Factura existente (Pendiente). Se actualizará a PAGADA al asignar.';
      feedbackElement.className = 'form-text text-start mt-1 text-warning';
    }
  } else {
    feedbackElement.innerHTML = '<i class="bi bi-check-circle-fill"></i> Nueva asignación (La factura se creará).';
    feedbackElement.className = 'form-text text-start mt-1 text-success';
  }
}, 500);

/**
 *
 * @param number
 * @returns
 */
const getMonth = (number: number) =>
  [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
  ][number];

/**
 * Pobla el selector con los últimos 12 meses (YYYY-MM)
 */
function populateMonthSelector() {
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    const month = String(monthIndex + 1).padStart(2, '0');
    const option = document.createElement('option');
    option.value = `${year}-${month}`;
    option.text = `${getMonth(monthIndex)} ${year}`;
    monthSelectorElement.appendChild(option);
  }
}

/**
 * Maneja el cambio de mes: consulta API y actualiza la tarjeta
 */
async function handleChangeMonthAssignedFolios() {
  const value = monthSelectorElement.value;

  // Actualiza el texto del mes en la tarjeta
  if (!value) {
    currentMountElement.innerText = getMonth(new Date().getMonth());
  } else {
    const [year, mm] = value.split('-');
    const monthIndex = Number(mm) - 1;
    currentMountElement.innerText = `${getMonth(monthIndex)} ${year}`;
  }

  // Consulta API con el mes seleccionado
  const data = await fetchFoliosByMonth(value || new Date().toISOString().slice(0, 7));
  if (!data) return;

  totalAssignCurrentMonthElement.innerText = String(
    data.assignedFoliosCurrentMonth ?? 0
  );
}

/**
 *
 * @param data
 * @returns
 */
const formatClients = (data: clientService.Client[]) =>
  data.map(
    ({ id, whmcsOrderId, fullName, email, phone, folios, api, blocked }) =>
      Object.values({
        id,
        name: fullName,
        email,
        phone: formatPhoneNumber(phone),
        folios,
        api: drawApiToggle(id, api),
        bloqueo: drawBlockToggle(id, blocked),
        acciones: drawShowAsingFolio(whmcsOrderId, id)
      }) as string[]
  );

// Definicion de tipos e interfaces
type ParamsSearch = {
  status: string;
  selectSearch: string;
  valueToSearch: string;
};
type AssingFoliosData = clientService.AssignFoliosOptions;
enum TypeInputOptions {
  ALL = 'all',
  ID = 'id',
  NAME = 'name',
  EMAIL = 'email',
  PHONE = 'phone',
}
enum TypeElementClicked {
  BUTTON = 'BUTTON',
  I = 'I',
}
